import { useEffect, useId } from 'react';
import { Link } from 'react-router-dom';

/** Small shared pieces: Spinner, LoadingPanel, EmptyState, Field, PageHeader, Badge, NotFound. */

export function Spinner() {
  return <span className="spinner" aria-hidden="true" />;
}

export function LoadingPanel({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="loading-panel" role="status">
      <Spinner />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({ title, hint, children }: { title: string; hint?: string; children?: React.ReactNode }) {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      {hint && <div>{hint}</div>}
      {children}
    </div>
  );
}

/** Labeled control with guaranteed htmlFor wiring: <Field label="…">{(id) => <input id={id} …/>}</Field> */
export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: (id: string) => React.ReactNode;
}) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children(id)}
      {hint && !error && <span className="hint">{hint}</span>}
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export function PageHeader({ title, sub, actions }: { title: string; sub?: React.ReactNode; actions?: React.ReactNode }) {
  useEffect(() => {
    document.title = `${title} · MHub`;
  }, [title]);
  return (
    <div className="page-header">
      <h1>{title}</h1>
      {sub && <span className="sub">{sub}</span>}
      {actions && <span className="end row">{actions}</span>}
    </div>
  );
}

/** Single source of truth for state → badge variant across both apps. */
const BADGE_VARIANTS: Record<string, string> = {
  approved: 'ok',
  published: 'ok',
  won: 'ok',
  delivered: 'ok',
  active: 'ok',
  connected: 'ok',
  in_review: 'warn',
  client_review: 'warn',
  paused: 'warn',
  rejected: 'danger',
  failed: 'danger',
  held: 'danger',
  lost: 'danger',
  churned: 'danger',
  generating: 'accent',
  validating: 'accent',
  executing: 'accent',
  sent: 'accent',
  queued: 'neutral',
  generated: 'neutral',
  draft: 'neutral',
  onboarding: 'neutral',
};

export function Badge({ state, label }: { state: string; label?: string }) {
  const variant = BADGE_VARIANTS[state] ?? 'neutral';
  return <span className={`badge badge--${variant}`}>{label ?? state.replaceAll('_', ' ')}</span>;
}

export function NotFound() {
  return (
    <div className="auth-card">
      <EmptyState title="Page not found" hint="This address doesn't match anything in MHub.">
        <p>
          <Link to="/app">Go to the ops workspace</Link> · <Link to="/portal">Go to the client portal</Link>
        </p>
      </EmptyState>
    </div>
  );
}
